/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/

import { atom } from "jotai";
import { BaseService } from "@/frameworks/libraries/restservice";

class PartnerAdminDefaultProfileImage extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/default_profile_images${id ? `/${id}` : ""}`;

	private static Cache = {
		Remove: atom<WebServices.PartnerAdmin.DefaultProfileImage.Remove.Response>({}),
		Detail: atom<WebServices.PartnerAdmin.DefaultProfileImage.Detail.Response>({}),
		Search: atom<WebServices.PartnerAdmin.DefaultProfileImage.Search.Response>({}),
		Add: atom<WebServices.PartnerAdmin.DefaultProfileImage.Add.Response>({}),
		Update: atom<WebServices.PartnerAdmin.DefaultProfileImage.Update.Response>({})
	};

	public remove = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id }: WebServices.PartnerAdmin.DefaultProfileImage.Remove.Request) =>
			this.delete(this.APIPath(id)),
		cache: PartnerAdminDefaultProfileImage.Cache.Remove
	});

	public detail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id }: WebServices.PartnerAdmin.DefaultProfileImage.Detail.Request) => this.get(this.APIPath(id)),
		cache: PartnerAdminDefaultProfileImage.Cache.Detail
	});

	public search = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.DefaultProfileImage.Search.Request) =>
			this.get(this.APIPath(), parameter),
		cache: PartnerAdminDefaultProfileImage.Cache.Search
	});

	public add = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.DefaultProfileImage.Add.Request) =>
			this.post(this.APIPath(), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: PartnerAdminDefaultProfileImage.Cache.Add
	});

	public update = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({
			id,
			parameter
		}: {
			id: string;
			parameter: WebServices.PartnerAdmin.DefaultProfileImage.Update.Request;
		}) =>
			this.put(this.APIPath(id), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: PartnerAdminDefaultProfileImage.Cache.Update
	});
}

class PartnerAdminDefaultUserStats extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/default_user_status${id ? `/${id}` : ""}`;

	private static Cache = {
		Remove: atom<WebServices.PartnerAdmin.DefaultUserStatus.Remove.Response>({}),
		Detail: atom<WebServices.PartnerAdmin.DefaultUserStatus.Detail.Response>({}),
		Search: atom<WebServices.PartnerAdmin.DefaultUserStatus.Search.Response>({}),
		Add: atom<WebServices.PartnerAdmin.DefaultUserStatus.Add.Response>({}),
		Update: atom<WebServices.PartnerAdmin.DefaultUserStatus.Update.Response>({})
	};

	public remove = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id }: WebServices.PartnerAdmin.DefaultUserStatus.Remove.Request) => this.delete(this.APIPath(id)),
		cache: PartnerAdminDefaultUserStats.Cache.Remove
	});

	public detail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id }: WebServices.PartnerAdmin.DefaultUserStatus.Detail.Request) => this.get(this.APIPath(id)),
		cache: PartnerAdminDefaultUserStats.Cache.Detail
	});

	public search = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.DefaultUserStatus.Search.Request) =>
			this.get(this.APIPath(), parameter),
		cache: PartnerAdminDefaultUserStats.Cache.Search
	});

	public add = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.DefaultUserStatus.Add.Request) =>
			this.post(this.APIPath(), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: PartnerAdminDefaultUserStats.Cache.Add
	});

	public update = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({
			id,
			parameter
		}: {
			id: string;
			parameter: WebServices.PartnerAdmin.DefaultUserStatus.Update.Request;
		}) =>
			this.put(this.APIPath(id), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: PartnerAdminDefaultUserStats.Cache.Update
	});
}

class PartnerAdminEmoticon extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/emoticons${id ? `/${id}` : ""}`;

	private static Cache = {
		Remove: atom<WebServices.PartnerAdmin.Emoticon.Remove.Response>({}),
		Detail: atom<WebServices.PartnerAdmin.Emoticon.Detail.Response>({}),
		List: atom<WebServices.PartnerAdmin.Emoticon.List.Response>({}),
		Add: atom<WebServices.PartnerAdmin.Emoticon.Add.Response>({}),
		Update: atom<WebServices.PartnerAdmin.Emoticon.Update.Response>({})
	};

	public remove = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id }: WebServices.PartnerAdmin.Emoticon.Remove.Request) => this.delete(this.APIPath(id)),
		cache: PartnerAdminEmoticon.Cache.Remove
	});

	public detail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id }: WebServices.PartnerAdmin.Emoticon.Detail.Request) => this.get(this.APIPath(id)),
		cache: PartnerAdminEmoticon.Cache.Detail
	});

	public list = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.Emoticon.List.Request) => this.get(this.APIPath(), parameter),
		cache: PartnerAdminEmoticon.Cache.List
	});

	public add = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.Emoticon.Add.Request) =>
			this.post(this.APIPath(), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: PartnerAdminEmoticon.Cache.Add
	});

	public update = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id, parameter }: { id: string; parameter: WebServices.PartnerAdmin.Emoticon.Update.Request }) =>
			this.post(this.APIPath(id), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: PartnerAdminEmoticon.Cache.Update
	});
}

class PartnerAdminSpaceMap extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/space_maps${id ? `/${id}` : ""}`;

	private static Cache = {
		Search: atom<WebServices.PartnerAdmin.SpaceMap.Search.Response>({}),
		Save: atom<WebServices.PartnerAdmin.SpaceMap.Save.Response>({}),
		Detail: atom<WebServices.PartnerAdmin.SpaceMap.Detail.Response>({}),
		Update: atom<WebServices.PartnerAdmin.SpaceMap.Update.Response>({}),
		Delete: atom<WebServices.PartnerAdmin.SpaceMap.Delete.Response>({}),
		Label: {
			Map: atom<WebServices.PartnerAdmin.SpaceMap.Label.Map.Response>({}),
			Scene: atom<WebServices.PartnerAdmin.SpaceMap.Label.Scene.Response>({}),
			Version: atom<WebServices.PartnerAdmin.SpaceMap.Label.MapVersion.Response>({})
		},
		SceneDetail: atom<WebServices.PartnerAdmin.SpaceMap.Label.SceneDetail.Response>({})
	};

	public search = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.SpaceMap.Search.Request) => this.get(this.APIPath(), parameter),
		cache: PartnerAdminSpaceMap.Cache.Search
	});

	public save = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.SpaceMap.Save.Request) => this.post(this.APIPath(), parameter),
		cache: PartnerAdminSpaceMap.Cache.Save
	});

	public detail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id, ...rest }: WebServices.PartnerAdmin.SpaceMap.Detail.Request) =>
			this.get(this.APIPath(id), rest),
		cache: PartnerAdminSpaceMap.Cache.Detail
	});

	public update = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id, ...rest }: WebServices.PartnerAdmin.SpaceMap.Update.Request) =>
			this.put(this.APIPath(id), rest),
		cache: PartnerAdminSpaceMap.Cache.Update
	});

	public remove = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id, ...rest }: WebServices.PartnerAdmin.SpaceMap.Delete.Request) =>
			this.delete(this.APIPath(id), rest),
		cache: PartnerAdminSpaceMap.Cache.Delete
	});

	public label = {
		map: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.PartnerAdmin.SpaceMap.Label.Map.Request) =>
				this.get(this.APIPath("labelValues"), parameter),
			cache: PartnerAdminSpaceMap.Cache.Label.Map
		}),
		scene: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ id, ...rest }: WebServices.PartnerAdmin.SpaceMap.Label.Scene.Request) =>
				this.get(this.APIPath(`labelValues/${id}`), rest),
			cache: PartnerAdminSpaceMap.Cache.Label.Scene
		}),
		version: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.PartnerAdmin.SpaceMap.Label.MapVersion.Request) =>
				this.get(this.APIPath("versions"), parameter),
			cache: PartnerAdminSpaceMap.Cache.Label.Version
		})
	};

	public sceneDetail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id, ...rest }: WebServices.PartnerAdmin.SpaceMap.Label.SceneDetail.Request) =>
			this.get(this.APIPath(`scenes/${id}`), rest),
		cache: PartnerAdminSpaceMap.Cache.SceneDetail
	});
}

class PartnerAdminSpaceTheme extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/space_themes${id ? `/${id}` : ""}`;

	private static Cache = {
		List: atom<WebServices.PartnerAdmin.SpaceTheme.List.Response>({}),
		Save: atom<WebServices.PartnerAdmin.SpaceTheme.Save.Response>({}),
		Detail: atom<WebServices.PartnerAdmin.SpaceTheme.Detail.Response>({}),
		Update: atom<WebServices.PartnerAdmin.SpaceTheme.Update.Response>({}),
		Delete: atom<WebServices.PartnerAdmin.SpaceTheme.Delete.Response>({}),
		Order: atom<WebServices.PartnerAdmin.SpaceTheme.Order.Response>({}),
		ListForOrder: atom<WebServices.PartnerAdmin.SpaceTheme.ListForOrder.Response>({})
	};

	public list = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.SpaceTheme.List.Request) =>
			this.get(this.APIPath(""), parameter),
		cache: PartnerAdminSpaceTheme.Cache.List
	});

	public listForOrder = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.SpaceTheme.ListForOrder.Request) =>
			this.get(this.APIPath("order"), parameter),
		cache: PartnerAdminSpaceTheme.Cache.ListForOrder
	});

	public save = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.SpaceTheme.Save.Request) =>
			this.post(this.APIPath(), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: PartnerAdminSpaceTheme.Cache.Save
	});

	public detail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id, ...rest }: WebServices.PartnerAdmin.SpaceTheme.Detail.Request) =>
			this.get(this.APIPath(id), rest),
		cache: PartnerAdminSpaceTheme.Cache.Detail
	});

	public update = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id, parameter }: { id: string; parameter: WebServices.PartnerAdmin.SpaceTheme.Update.Request }) =>
			this.post(this.APIPath(id), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: PartnerAdminSpaceTheme.Cache.Update
	});
	public remove = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id, ...rest }: WebServices.PartnerAdmin.SpaceTheme.Delete.Request) =>
			this.delete(this.APIPath(id), rest),
		cache: PartnerAdminSpaceTheme.Cache.Delete
	});

	public order = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.SpaceTheme.Order.Request) =>
			this.post(this.APIPath("order"), parameter),
		cache: PartnerAdminSpaceTheme.Cache.Order
	});
}

class Partner extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/partners${id ? `/${id}` : ""}`;

	private static Cache = {
		List: atom<WebServices.Partner.List.Response>({}),
		Detail: atom<WebServices.Partner.Detail.Response>({}),
		Add: atom<WebServices.Partner.Add.Response>({})
	};

	public list = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.Partner.List.Request) => this.get(this.APIPath(), parameter),
		cache: Partner.Cache.List
	});

	public detail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id }: WebServices.Partner.Detail.Request) => this.get(this.APIPath(id)),
		cache: Partner.Cache.Detail
	});

	public add = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.Partner.Add.Request) => this.post(this.APIPath(), parameter),
		cache: Partner.Cache.Add
	});
}

class PartnerUser extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_users${id ? `/${id}` : ""}`;

	private static Cache = {
		List: atom<WebServices.PartnerUser.List.Response>({}),
		Detail: atom<WebServices.PartnerUser.Detail.Response>({}),
		Add: atom<WebServices.PartnerUser.Add.Response>({})
	};

	public list = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerUser.List.Request) => this.get(this.APIPath(), parameter),
		cache: PartnerUser.Cache.List
	});

	public detail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id }: WebServices.PartnerUser.Detail.Request) => this.get(this.APIPath(id)),
		cache: PartnerUser.Cache.Detail
	});

	public add = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerUser.Add.Request) => this.post(this.APIPath(), parameter),
		cache: PartnerUser.Cache.Add
	});
}

class PartnerOpenWorld extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/open_worlds${id ? `/${id}` : ""}`;

	private static Cache = {
		Save: atom<WebServices.PartnerAdmin.OpenWorld.Save.Response>({}),
		Update: atom<WebServices.PartnerAdmin.OpenWorld.Update.Response>({}),
		Remove: atom<WebServices.PartnerAdmin.OpenWorld.Remove.Response>({})
	};

	public save = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.OpenWorld.Save.Request) =>
			this.post(this.APIPath(), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: PartnerOpenWorld.Cache.Save
	});

	public update = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id, parameter }: { id: string; parameter: WebServices.PartnerAdmin.OpenWorld.Update.Request }) =>
			this.post(this.APIPath(id), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: PartnerOpenWorld.Cache.Update
	});

	public remove = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id, parameter }: { id: string; parameter: WebServices.PartnerAdmin.OpenWorld.Update.Request }) =>
			this.delete(this.APIPath(id), parameter),
		cache: PartnerOpenWorld.Cache.Remove
	});
}

class PartnerAdvertisement extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/advertisements${id ? `/${id}` : ""}`;

	private static Cache = {
		Save: atom<WebServices.PartnerAdmin.Government.Advertisements.Save.Response>({}),
		Update: atom<WebServices.PartnerAdmin.Government.Advertisements.Update.Response>({}),
		List: atom<WebServices.PartnerAdmin.Government.Advertisements.List.Response>({}),
		Detail: atom<WebServices.PartnerAdmin.Government.Advertisements.Detail.Response>({})
	};

	public save = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.Government.Advertisements.Save.Request) =>
			this.post(this.APIPath(), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: PartnerAdvertisement.Cache.Save
	});

	public update = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({
			id,
			parameter
		}: {
			id: string;
			parameter: WebServices.PartnerAdmin.Government.Advertisements.Update.Request;
		}) =>
			this.post(this.APIPath(id), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: PartnerAdvertisement.Cache.Update
	});

	public list = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id }: WebServices.PartnerAdmin.Government.Advertisements.List.Request) =>
			this.get(this.APIPath(id)),
		cache: PartnerAdvertisement.Cache.List
	});

	public detail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id }: WebServices.PartnerAdmin.Government.Advertisements.Detail.Request) =>
			this.get(this.APIPath(id)),
		cache: PartnerAdvertisement.Cache.Detail
	});
}

class PartnerDailyGift extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/daily_gift${id ? `/${id}` : ""}`;

	private static Cache = {
		Save: atom<WebServices.PartnerAdmin.Government.DailyGift.Save.Response>({})
	};

	public save = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.PartnerAdmin.Government.DailyGift.Save.Request) =>
			this.post<WebServices.PartnerAdmin.Government.DailyGift.Save.Response>(this.APIPath(), parameter),
		cache: PartnerDailyGift.Cache.Save
	});
}

class PartnerServiceStatic {
	constructor(
		public readonly partnerAdmin = {
			defaultProfileImage: new PartnerAdminDefaultProfileImage(),
			defaultUserStatus: new PartnerAdminDefaultUserStats(),
			emoticon: new PartnerAdminEmoticon(),
			spaceMap: new PartnerAdminSpaceMap(),
			spaceTheme: new PartnerAdminSpaceTheme(),
			openWorld: new PartnerOpenWorld(),
			advertisement: new PartnerAdvertisement(),
			dailyGift: new PartnerDailyGift()
		},
		public readonly partner = new Partner(),
		public readonly partnerUser = new PartnerUser()
	) {}
}

const PartnerService = new PartnerServiceStatic();
export default PartnerService;
